import React, { useEffect, useState, useRef } from 'react';
import { getProductById } from '../../services/productService';
import { formatPrice } from '../../lib/utils';
import './ProductDetailsModal.css';

const decodeHtml = (html) => {
  if (typeof document === 'undefined') return html;
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

const PLACEHOLDER_IMAGE = 'https://placehold.co/100x100?text=No+Image';

const getProductImage = (product) => {
  if (product?.image_urls?.[0]) return product.image_urls[0];
  const defaultVariant =
    product?.variants?.find((v) => v.is_default) ?? product?.variants?.[0];
  const defaultImage =
    defaultVariant?.images?.find((i) => i.is_default) ?? defaultVariant?.images?.[0];
  return defaultImage?.image_url ?? PLACEHOLDER_IMAGE;
};

const getDisplayVariants = (variants = []) => {
  return variants.length > 1 ? variants : [];
};

const getSingleVariantPrice = (product) => {
  const defaultVariant =
    product?.variants?.find((v) => v.is_default) ?? product?.variants?.[0];

  return defaultVariant?.retail_price_money ?? product?.retail_price ?? null;
};

const ProductDetailsModal = ({ isOpen, onClose, productId }) => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isMounted = useRef(false);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (isOpen && productId) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);
        setProduct(null);

        try {
          const productData = await getProductById(productId);
          if (!isMounted.current) return;
          if (productData) {
            setProduct(productData);
          } else {
            setError("Proizvod nije pronađen.");
          }
        } catch {
          if (isMounted.current) {
            setError("Došlo je do greške prilikom učitavanja podataka.");
          }
        } finally {
          if (isMounted.current) {
            setLoading(false);
          }
        }
      };

      fetchData();
    }
  }, [isOpen, productId]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const displayVariants = getDisplayVariants(product?.variants);
  const singleVariantPrice = getSingleVariantPrice(product);

  return (
    <div className={`product-modal-overlay ${isOpen ? 'open' : ''}`} onClick={handleOverlayClick}>
      <div className="product-modal-content">
        <button className="product-modal-close" onClick={onClose}>&times;</button>

        {loading ? (
          <div className="loading-spinner">Učitavanje...</div>
        ) : error ? (
          <div className="loading-spinner">{error}</div>
        ) : product ? (
          <div className="product-modal-body">
            <div className="product-modal-image-col">
              <div style={{ textAlign: 'center' }}>
                <img
                  src={getProductImage(product)}
                  alt={product.name}
                  className="product-modal-image"
                />
                <h2 className="product-modal-title" style={{ marginTop: '20px' }}>{product.name}</h2>
              </div>
            </div>

            <div className="product-modal-info-col">
              <div className="product-modal-desc">
                {product.description ? (
                  <span dangerouslySetInnerHTML={{ __html: decodeHtml(product.description) }} />
                ) : (
                  'Opis će biti dostupan uskoro.'
                )}
              </div>

              <div className="product-modal-pricing">
                {displayVariants.length > 0 ? (
                  <div className="variants-table-container">
                    <table className="variants-table">
                      <thead>
                        <tr>
                          {displayVariants.map(variant => (
                            <th key={variant.id}>{variant.name}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {displayVariants.map(variant => (
                            <td key={variant.id}>
                              {variant.retail_price_money?.amount
                                ? formatPrice(
                                    variant.retail_price_money.amount,
                                    variant.retail_price_money.currency_code || product.retail_price?.currency_code || 'RSD'
                                  )
                                : 'Cena će biti dostupna uskoro'}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="product-modal-price">
                    {singleVariantPrice?.amount
                      ? formatPrice(
                          singleVariantPrice.amount,
                          singleVariantPrice.currency_code || 'RSD'
                        )
                      : 'Cena na upit'}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ProductDetailsModal;
